import { useCallback, useRef, useState } from 'react';
import { validateTaxNumber } from 'utils/taxValidationApi';

// RAT-1911: owns the "ask the tax registry about this number" call and its
// result, so the Legal section only has to render it. The check runs on blur
// (never per keystroke) and the same tax number + legal name + country is never
// asked twice in a row, because the backend charges a real registry call for it.
export const useTaxNumberValidation = () => {
  const [result, setResult] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  // Which field the registry answered about last time. Deliberately kept across
  // a reset, so the "checking..." line shows under the field the answer will
  // land under - for a non-Spanish merchant that is the VAT number, and putting
  // the spinner under the Tax ID would point at the wrong field for 5 seconds.
  const [verdictField, setVerdictField] = useState(null);
  const lastCheckedKeyRef = useRef(null);

  // Dropping the result also clears the key, so editing a field and typing the
  // previous value back still re-checks rather than showing a stale verdict.
  const reset = useCallback(() => {
    lastCheckedKeyRef.current = null;
    setResult(null);
  }, []);

  const check = useCallback(
    async (input) => {
      const taxId = (input?.taxId || '').trim();
      const vatNumber = (input?.vatNumber || '').trim();
      if (!taxId && !vatNumber) {
        lastCheckedKeyRef.current = null;
        setResult(null);
        return null;
      }

      const key = [
        taxId,
        vatNumber,
        (input?.legalName || '').trim(),
        input?.countryCode || '',
        input?.country || '',
      ].join('|');
      if (lastCheckedKeyRef.current === key) {
        return result;
      }
      lastCheckedKeyRef.current = key;

      setIsChecking(true);
      const outcome = await validateTaxNumber({ ...input, taxId, vatNumber });
      setIsChecking(false);
      // A failed request releases the key: the operator can blur again to retry.
      if (!outcome) {
        lastCheckedKeyRef.current = null;
      }
      if (outcome?.verdictField) {
        setVerdictField(outcome.verdictField);
      }
      setResult(outcome);
      return outcome;
    },
    [result],
  );

  return { result, isChecking, verdictField, check, reset };
};
